import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Lightning } from '@phosphor-icons/react';
import { API_BASE_URL } from '@/config/api';
import {
  KNOWN_MARKET_STATUSES,
  deriveMarketViewState,
  normalizeMarketResponse,
  buildMarketQueryKey,
  marketCacheMatchesAddress,
  marketCacheMatchesLocale,
} from '@/utils/marketData';
import { resolveTypeBand, resolveStreetBand } from '@/utils/marketBands';

const API = API_BASE_URL;
const MIN_ADDRESS_LEN = 5;
const FETCH_DEBOUNCE_MS = 600;

function isAiMarketResult(result) {
  return result?.data_type === 'ai_estimate' || result?.source === 'AI Estimate';
}

function canEstimateLocation({ address, postcode, city }) {
  const addr = (address || '').trim();
  if (addr.length >= MIN_ADDRESS_LEN) return true;
  return Boolean(postcode || city);
}

function toErrorResult(message) {
  return normalizeMarketResponse({
    status: 'error',
    message,
    source: 'AI Estimate',
    data_type: 'ai_estimate',
  });
}

function clamp(value, min, max) {
  if (value == null || Number.isNaN(value)) return min;
  if (min == null || max == null) return value;
  return Math.min(Math.max(value, min), max);
}

function snapToStep(value, min, max, step = 50) {
  if (value == null || min == null || max == null) return value;
  const snapped = Math.round(value / step) * step;
  return clamp(snapped, min, max);
}

function cacheUsable(cached, address, country, opCur) {
  return marketCacheMatchesAddress(cached, address)
    && marketCacheMatchesLocale(cached, { countryCode: country, operatingCurrency: opCur });
}

/**
 * Market Reference — street / location evaluation.
 * Quotes are in `quote_currency` from the API (CHF for CH; operating currency otherwise).
 * Apply writes pricePerSqm in operating currency — converting via FX only when needed.
 */
export function MarketPrice({
  postcode,
  city,
  address = '',
  addressLabel = '',
  currentPrice,
  onApplyAssumptions,
  cachedResult,
  onResultLoaded,
  refreshKey,
  requestedLocationLabel = '',
  operatingCurrency = 'CHF',
  countryCode = 'CH',
  fx = null,
}) {
  const opCur = (operatingCurrency || 'CHF').toUpperCase();
  const country = (countryCode || 'CH').toUpperCase();
  const fxRate = (fx && Number(fx.rate) > 0) ? Number(fx.rate) : 1;
  const [result, setResult] = useState(() => (
    cacheUsable(cachedResult, address, (countryCode || 'CH').toUpperCase(), (operatingCurrency || 'CHF').toUpperCase())
      ? normalizeMarketResponse(cachedResult)
      : null
  ));
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [streetDraft, setStreetDraft] = useState(null);

  const lastFetchKey = useRef(
    cacheUsable(cachedResult, address, (countryCode || 'CH').toUpperCase(), (operatingCurrency || 'CHF').toUpperCase())
      ? buildMarketQueryKey(address)
      : ''
  );
  const lastRefreshKey = useRef(refreshKey || 0);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const requestSeq = useRef(0);
  const fetchable = canEstimateLocation({ address, postcode, city });
  const addressKey = buildMarketQueryKey(address || addressLabel);

  /** Currency of the displayed bands (from latest result, else request intent). */
  const sourceCur = (result?.quote_currency || (country === 'CH' ? 'CHF' : opCur)).toUpperCase();
  const perSqm = `${sourceCur}/m²`;
  const perSqmOps = `${opCur}/m²`;

  const quoteToOps = useCallback((quoteValue) => {
    const n = Number(quoteValue);
    if (!Number.isFinite(n)) return 0;
    if (sourceCur === opCur) return Math.round(n);
    // Swiss SSOT path: quotes in CHF, FX is 1 ops = rate CHF → ops = CHF / rate.
    if (sourceCur === 'CHF' && opCur !== 'CHF' && fxRate > 0) {
      return Math.round(n / fxRate);
    }
    // Same as ops already handled; unknown cross pairs: apply raw (backend should quote in ops).
    return Math.round(n);
  }, [sourceCur, opCur, fxRate]);

  const publish = useCallback((normalized) => {
    const withKey = {
      ...normalized,
      query_key: buildMarketQueryKey(address || addressLabel),
    };
    setResult(withKey);
    if (onResultLoaded) onResultLoaded(withKey);
  }, [address, addressLabel, onResultLoaded]);

  const fetchPrice = useCallback(async () => {
    if (!canEstimateLocation({ address, postcode, city })) return;
    const key = buildMarketQueryKey(address || addressLabel);
    lastFetchKey.current = key;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeq.current;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/market-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          postal_code: postcode || undefined,
          city: city || undefined,
          property_type: 'apartment',
          address: (address || addressLabel || '').trim() || undefined,
          country_code: country,
          currency: country === 'CH' ? 'CHF' : opCur,
        }),
      });
      if (seq !== requestSeq.current) return;

      const data = await res.json().catch(() => null);
      if (seq !== requestSeq.current) return;

      if (!res.ok) {
        const detail = (data && (data.message || data.detail)) || `HTTP ${res.status}`;
        publish(toErrorResult(typeof detail === 'string' ? detail : 'Market estimate failed'));
        return;
      }
      if (!data) {
        publish(toErrorResult('Invalid response from market estimate'));
        return;
      }
      publish(normalizeMarketResponse(data));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (seq !== requestSeq.current) return;
      publish(toErrorResult(err?.message ? `Estimate failed: ${err.message}` : 'Network error'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [address, addressLabel, postcode, city, country, opCur, publish]);

  const scheduleFetch = useCallback(() => {
    clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      fetchPrice();
    }, FETCH_DEBOUNCE_MS);
  }, [fetchPrice]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!fetchable || !addressKey) {
      setLoading(false);
      return undefined;
    }

    if (cacheUsable(cachedResult, address || addressLabel, country, opCur)
      || cacheUsable(result, address || addressLabel, country, opCur)) {
      if (cacheUsable(cachedResult, address || addressLabel, country, opCur)) {
        const restored = normalizeMarketResponse(cachedResult);
        setResult({
          ...restored,
          query_key: cachedResult.query_key || restored.query_key || addressKey,
        });
      }
      lastFetchKey.current = addressKey;
      setLoading(false);
      return undefined;
    }

    if (lastFetchKey.current === addressKey && isAiMarketResult(result) && result?.status === 'ok') {
      setLoading(false);
      return undefined;
    }

    scheduleFetch();
    return () => clearTimeout(debounceRef.current);
  }, [addressKey, fetchable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshKey !== undefined && refreshKey !== lastRefreshKey.current) {
      lastRefreshKey.current = refreshKey;
      lastFetchKey.current = '';
      setResult(null);
      setSelected(null);
      if (fetchable && addressKey) {
        scheduleFetch();
      }
    }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Country / operating currency change → drop CHF-framed cache and re-estimate locally.
  const localeKey = `${country}|${opCur}`;
  const lastLocaleKey = useRef(localeKey);
  useEffect(() => {
    if (lastLocaleKey.current === localeKey) return;
    lastLocaleKey.current = localeKey;
    lastFetchKey.current = '';
    setSelected(null);
    if (onResultLoaded) onResultLoaded(null);
    setResult(null);
    if (fetchable && addressKey) {
      scheduleFetch();
    }
  }, [localeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cachedResult) return;
    if (!marketCacheMatchesLocale(cachedResult, { countryCode: country, operatingCurrency: opCur })) {
      return;
    }
    const normalized = normalizeMarketResponse(cachedResult);
    if (KNOWN_MARKET_STATUSES.has(normalized?.status) && normalized.status === 'ok' && !isAiMarketResult(normalized)) {
      return;
    }
    if (cacheUsable(cachedResult, address || addressLabel, country, opCur)
      || !result
      || result.status !== 'ok') {
      setResult({
        ...normalized,
        query_key: cachedResult.query_key || normalized.query_key || buildMarketQueryKey(address || addressLabel),
      });
      if (cachedResult.query_key) lastFetchKey.current = cachedResult.query_key;
    }
  }, [cachedResult]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const aptBand = useMemo(() => resolveTypeBand(result, 'apartment'), [result]);
  const houseBand = useMemo(() => resolveTypeBand(result, 'house'), [result]);
  const streetBand = useMemo(() => resolveStreetBand(result), [result]);

  // Seed selection from street mid (or project price if already inside band).
  // currentPrice is in operating currency; convert to quote units when needed for compare/seed.
  useEffect(() => {
    if (result?.status !== 'ok' || streetBand.mid == null) return;
    const projectOps = Number(currentPrice);
    let projectInQuote = null;
    if (Number.isFinite(projectOps) && projectOps > 0) {
      if (sourceCur === opCur) {
        projectInQuote = projectOps;
      } else if (sourceCur === 'CHF' && opCur !== 'CHF' && fxRate > 0) {
        projectInQuote = projectOps * fxRate;
      } else {
        projectInQuote = projectOps;
      }
    }
    let seed = streetBand.mid;
    if (projectInQuote != null
      && streetBand.min != null && streetBand.max != null
      && projectInQuote >= streetBand.min && projectInQuote <= streetBand.max) {
      seed = projectInQuote;
    }
    setSelected((prev) => (prev == null ? seed : prev));
  }, [result, streetBand, currentPrice, sourceCur, opCur, fxRate]);

  const handleApplyStreet = () => {
    const quoteValue = selected ?? streetBand.mid;
    if (!quoteValue || !onApplyAssumptions) return;
    onApplyAssumptions({
      pricePerSqm: quoteToOps(quoteValue),
      apartmentPricePerSqm: 0,
      housePricePerSqm: 0,
    });
  };

  if (!fetchable) return null;
  const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('de-CH'));
  const viewState = deriveMarketViewState({ loading, result });
  const hasData = viewState === 'ok';
  const hasNoData = viewState === 'not_found';
  const hasBlocked = viewState === 'blocked';
  const hasError = viewState === 'error';
  const hasUnknown = viewState === 'unknown';
  const hasIdle = viewState === 'idle';
  const providerLabel = result?.source || null;
  const usedFallback = Array.isArray(result?.providers_tried) && result.providers_tried.length > 1;
  const requestLocationLabel = (address || addressLabel || '').trim()
    || [postcode, city].filter(Boolean).join(' ').trim()
    || requestedLocationLabel
    || 'Unknown location';
  const confirmedLocationLabel = result?.location || requestLocationLabel;
  const compactAddressLabel = (address || addressLabel)
    ? (address || addressLabel).split(',').slice(0, 2).join(',').trim()
    : '';
  const locationEvidence = requestedLocationLabel || compactAddressLabel || requestLocationLabel;
  const isAiEstimate = isAiMarketResult(result);

  const display = selected ?? streetBand.mid;
  const ready = streetBand.mid != null && streetBand.min != null && streetBand.max != null
    && streetBand.min < streetBand.max;
  const singlePoint = streetBand.mid != null && streetBand.min != null && streetBand.max != null
    && streetBand.min === streetBand.max;
  const projectOps = Number(currentPrice);
  let projectInQuote = null;
  if (Number.isFinite(projectOps) && projectOps > 0) {
    if (sourceCur === opCur) projectInQuote = projectOps;
    else if (sourceCur === 'CHF' && opCur !== 'CHF' && fxRate > 0) projectInQuote = projectOps * fxRate;
    else projectInQuote = projectOps;
  }
  const hasProject = projectInQuote != null && streetBand.mid != null;
  const vsStreetPct = hasProject
    ? Math.round(((projectInQuote - streetBand.mid) / streetBand.mid) * 100)
    : null;
  const needsFxOnApply = sourceCur !== opCur && sourceCur === 'CHF';
  const applyTitle = !display
    ? ''
    : (sourceCur === opCur
      ? `Apply ${fmt(display)} ${perSqm} as the project sale price`
      : needsFxOnApply
        ? `Apply ${fmt(display)} ${perSqm} → ${fmt(quoteToOps(display))} ${perSqmOps} (FX ${fxRate})`
        : `Apply ${fmt(quoteToOps(display))} ${perSqmOps}`);
  const applyHint = sourceCur === opCur
    ? ' Apply sets the left-panel project price (one price for the deal).'
    : needsFxOnApply
      ? ` Apply converts to ${perSqmOps} via FX before writing the left-panel price.`
      : ` Apply writes ${perSqmOps} to the left-panel project price.`;

  return (
    <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-2" data-testid="market-price-suggestion">
      <div className="flex flex-col gap-1 mb-1">
        <div className="flex flex-wrap items-center gap-1">
          <Lightning weight="fill" className="w-3 h-3 text-indigo-500" />
          <span className="text-[8px] font-bold text-indigo-600 uppercase">Market Reference</span>
          {providerLabel && (
            <span
              className="text-[7px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1 py-[1px]"
              data-testid="market-source-badge"
              title={isAiEstimate ? `Finished residential saleable ${perSqm} (AI)` : (usedFallback ? 'Value resolved via fallback provider chain' : 'Value resolved from primary provider')}
            >
              {providerLabel}{usedFallback && !isAiEstimate ? ' · fallback' : ''}
            </span>
          )}
        </div>
        <div className="space-y-0.5" data-testid="market-location-confirmation">
          <div className="text-[9px] md:text-[8px] text-indigo-700 bg-indigo-100/80 border border-indigo-200 rounded px-1.5 py-1 leading-tight break-words">
            <span className="font-bold">Location used:</span>{' '}
            <span className="font-semibold">{requestLocationLabel}</span>
          </div>
          <div className="text-[8px] text-indigo-600/90 break-words">
            <span className="font-semibold">Address hint:</span>{' '}
            <span>{locationEvidence || 'N/A'}</span>
            {confirmedLocationLabel && confirmedLocationLabel !== requestLocationLabel && (
              <>
                <span className="mx-1 text-indigo-300">•</span>
                <span className="font-semibold">Provider says:</span>{' '}
                <span>{confirmedLocationLabel}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="text-[8px] text-indigo-500 animate-pulse">Estimating street {perSqm}…</div>}

      {hasData && (
        <>
          {(result.data_type === 'ai_estimate' || result.data_type === 'fallback_estimate' || result.data_type === 'default_estimate') && (
            <div className="text-[7px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mb-1 leading-tight" data-testid="market-fallback-banner">
              <span className="font-bold">
                {result.data_type === 'ai_estimate'
                  ? `AI street estimate — finished residential saleable ${perSqm}. `
                  : result.data_type === 'default_estimate'
                    ? 'Indicative only — not live market data. '
                    : 'Fallback estimate. '}
              </span>
              {result.message || `Use as a street check against your single project ${perSqm} (left panel).`}
            </div>
          )}

          <div
            className="mb-1.5 overflow-hidden rounded border border-indigo-200/80 bg-white/70"
            data-testid="market-type-table"
          >
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-indigo-100 text-[7px] uppercase tracking-wide text-indigo-500">
                  <th className="px-1.5 py-1 font-semibold">Street context</th>
                  <th className="px-1.5 py-1 font-semibold text-right">Average {perSqm}</th>
                </tr>
              </thead>
              <tbody className="text-[8px] text-indigo-800">
                <tr className="border-b border-indigo-50" data-testid="market-row-apartment">
                  <td className="px-1.5 py-1">Apartment (ref)</td>
                  <td className="px-1.5 py-1 text-right tabular-nums">{fmt(aptBand.mid)}</td>
                </tr>
                <tr className="border-b border-indigo-50" data-testid="market-row-house">
                  <td className="px-1.5 py-1">House (ref)</td>
                  <td className="px-1.5 py-1 text-right tabular-nums">{fmt(houseBand.mid)}</td>
                </tr>
                <tr data-testid="market-row-street">
                  <td className="px-1.5 py-1 font-semibold text-indigo-900">Street average</td>
                  <td className="px-1.5 py-1 text-right tabular-nums font-semibold text-indigo-900">
                    {fmt(streetBand.mid ?? result.street_average)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {streetBand.mid == null ? (
            <div className="text-[7px] text-amber-700" data-testid="market-missing-street">
              No street {perSqm} — Refresh Land Data.
            </div>
          ) : (
            <div className="rounded border border-indigo-300 bg-white/90 p-1.5 space-y-1" data-testid="market-street-panel">
              <div className="flex items-baseline justify-between gap-1">
                <div>
                  <span className="text-[11px] font-bold tabular-nums text-indigo-900" data-testid="market-selected-street">
                    {fmt(display)}
                  </span>
                  <span className="text-[7px] text-indigo-500 ml-1">{perSqm} selected</span>
                </div>
                <div className="text-[7px] text-indigo-500 tabular-nums text-right">
                  Street mid <span className="font-semibold text-indigo-700">{fmt(streetBand.mid)}</span>
                </div>
              </div>

              {hasProject && (
                <div
                  className={`text-[8px] leading-tight ${vsStreetPct > 5 ? 'text-amber-700' : vsStreetPct < -5 ? 'text-emerald-700' : 'text-indigo-700'}`}
                  data-testid="market-vs-project"
                >
                  Your project <span className="font-semibold tabular-nums">{fmt(projectInQuote)}</span>
                  {' '}·{' '}
                  {vsStreetPct === 0
                    ? 'in line with street mid'
                    : `${vsStreetPct > 0 ? '+' : ''}${vsStreetPct}% vs street mid`}
                </div>
              )}

              {ready && (
                <div className="space-y-0.5" data-testid="market-slider-street">
                  <input
                    type="range"
                    min={streetBand.min}
                    max={streetBand.max}
                    step={50}
                    value={clamp(display, streetBand.min, streetBand.max)}
                    onChange={(e) => {
                      const next = snapToStep(Number(e.target.value), streetBand.min, streetBand.max);
                      setSelected(next);
                    }}
                    aria-label={`Street ${perSqm} slider`}
                    className="market-chf-slider w-full h-2 cursor-pointer appearance-none rounded-full outline-none"
                    style={{
                      background: 'linear-gradient(90deg, #bae6fd 0%, #818cf8 50%, #c4b5fd 100%)',
                    }}
                  />
                  <div className="flex justify-between text-[7px] tabular-nums text-indigo-400">
                    <span>{fmt(streetBand.min)}</span>
                    <span className="text-indigo-600 font-medium">Street {fmt(streetBand.mid)}</span>
                    <span>{fmt(streetBand.max)}</span>
                  </div>
                </div>
              )}

              {(ready || singlePoint) && (
                <div className="flex items-center gap-1">
                  <label className="sr-only" htmlFor="market-num-street">{`Street precise ${perSqm}`}</label>
                  <input
                    id="market-num-street"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    min={streetBand.min ?? 0}
                    max={streetBand.max ?? undefined}
                    value={streetDraft ?? (display ?? '')}
                    onFocus={(e) => {
                      const current = display ?? 0;
                      setStreetDraft(Number(current) === 0 ? '' : String(current));
                      e.target.select();
                    }}
                    onBlur={() => {
                      if (streetDraft !== null) {
                        const raw = Number(streetDraft);
                        if (Number.isFinite(raw)) {
                          setSelected(clamp(raw, streetBand.min ?? 0, streetBand.max ?? raw));
                        }
                      }
                      setStreetDraft(null);
                    }}
                    onChange={(e) => {
                      const rawText = e.target.value.replace(',', '.');
                      if (rawText !== '' && !/^\d*\.?\d*$/.test(rawText)) return;
                      setStreetDraft(rawText);
                      const raw = Number(rawText);
                      if (!Number.isFinite(raw)) return;
                      setSelected(clamp(raw, streetBand.min ?? 0, streetBand.max ?? raw));
                    }}
                    className="w-full min-h-[28px] text-[9px] tabular-nums rounded border border-indigo-200 px-1.5 py-1 bg-white"
                    data-testid="market-input-street"
                  />
                  <button
                    type="button"
                    onClick={handleApplyStreet}
                    disabled={!display}
                    className="shrink-0 text-[8px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 px-2 py-1 rounded min-h-[28px]"
                    data-testid="market-apply-street"
                    title={applyTitle}
                  >
                    Apply to project
                  </button>
                </div>
              )}

              <div className="text-[7px] text-indigo-500 leading-tight">
                Market quotes are <span className="font-semibold">{perSqm}</span>.
                {applyHint}
              </div>
            </div>
          )}
        </>
      )}

      {hasNoData && <div className="text-[8px] text-indigo-400">{result?.message || 'No data for this area'}</div>}
      {hasBlocked && <div className="text-[8px] text-amber-600">{result?.message || 'Source blocked, retry or change address/city'}</div>}
      {hasError && (
        <div className="text-[8px] text-red-500 flex items-center gap-1" data-testid="market-price-error">
          <span>{result?.message || 'Market data unavailable'}</span>
          <button
            type="button"
            onClick={() => {
              lastFetchKey.current = '';
              fetchPrice();
            }}
            className="underline underline-offset-2 text-indigo-700"
            data-testid="market-price-retry-error"
          >
            Retry
          </button>
        </div>
      )}
      {hasIdle && (
        <div className="text-[8px] text-indigo-500 flex items-center gap-1">
          <span>No market estimate loaded yet.</span>
          <button
            onClick={() => {
              lastFetchKey.current = '';
              fetchPrice();
            }}
            className="underline underline-offset-2 text-indigo-700"
            data-testid="market-price-fetch"
          >
            Fetch
          </button>
        </div>
      )}
      {hasUnknown && (
        <div className="text-[8px] text-indigo-500 flex items-center gap-1">
          <span>Stale market cache detected.</span>
          <button
            onClick={() => {
              lastFetchKey.current = '';
              fetchPrice();
            }}
            className="underline underline-offset-2 text-indigo-700"
            data-testid="market-price-retry"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
